import { BoundSelectField, Button, ButtonMenu, Css, IconButton, Palette } from "@homebound/beam";
import { FieldState, ListFieldState, ObjectConfig, ObjectState } from "@homebound/form-state";
import { observer } from "mobx-react";
import { ReactNode } from "react";
import { Icon } from "src/components";
import { BoundAiNumberField } from "src/components/formFields/ai";
import { type AiFieldProposalFormValue } from "src/components/formFields/ai/utils/aiFieldProposalForm";
import { rejectAiProposal } from "src/components/formFields/ai/utils/bindAiProposal";
import {
  IncrementalCollectionOp,
  Maybe,
  SaveProgramDataInput,
  SaveReadyPlanProgramDataInput,
} from "src/generated/graphqlTypes";
import {
  PlanPackageOptionsV2_OptionFragment,
  PlanPackageOptionsV2_PlanPackageFragment,
  PlanPackageOptionsV2_ProgramDataFragment,
  PlanPackageOptionsV2Query,
} from "src/routes/libraries/plan-package-v2/components/OptionsTab.generated";

type EnumDetails = PlanPackageOptionsV2Query["enumDetails"];
type EnumName = keyof Omit<EnumDetails, "__typename">;
/** An impact value is a number for numeric fields, or the enum code for enum fields. */
type ImpactValue = number | string;
type ProgramDataImpactField = {
  key: keyof SaveProgramDataInput & keyof PlanPackageOptionsV2_ProgramDataFragment;
  label: string;
  unit?: "SF" | "FT";
  enumName?: EnumName;
};

export type ImpactElevationRow = { elevationId: Maybe<string>; impact: Maybe<ImpactValue> };
export type ImpactRow = { fieldKey: Maybe<string>; impact: Maybe<ImpactValue>; elevations: ImpactElevationRow[] };

export const impactsConfig: ObjectConfig<ImpactRow> = {
  fieldKey: { type: "value" },
  impact: { type: "value" },
  elevations: { type: "list", config: { elevationId: { type: "value" }, impact: { type: "value" } } },
};

type ProgramDataImpactsSectionProps = {
  option: PlanPackageOptionsV2_OptionFragment;
  planPackage: PlanPackageOptionsV2_PlanPackageFragment;
  enumDetails: EnumDetails;
  impacts: ListFieldState<ImpactRow>;
  /** The option's live proposal statuses, so the square-footage rows can diff and resolve theirs. */
  fieldProposals: readonly ObjectState<AiFieldProposalFormValue>[];
  disabled: ReactNode | boolean;
};

/**
 * The square-footage fields the import proposes for an option, i.e. the only impacts that can carry a
 * proposal. Extraction gives a single sellable number and leaves below-ground footage alone, so it
 * lands on `sellableAboveGroundSqft` rather than the derived `sellableSqft` total.
 */
export const optionSqftFields = ["sellableAboveGroundSqft", "netBuildableSqft", "grossBuildableSqft"] as const;

/** How an option changes the plan's program data. */
export const ProgramDataImpactsSection = observer(function ProgramDataImpactsSection(
  props: ProgramDataImpactsSectionProps,
) {
  const { option, planPackage, enumDetails, impacts, fieldProposals, disabled } = props;
  const usedKeys = impacts.rows.map((row) => row.fieldKey.value).compact();
  const elevations = planPackage.options.filter((o) => o.type.isElevation);
  return (
    <div css={Css.df.fdc.gap1.$}>
      {impacts.rows.map((row) => {
        const field = programDataImpactFields.find((f) => f.key === row.fieldKey.value);
        // Not `findAiFieldProposal`: that answers a DELETE proposal's `active` for any field name,
        // and editing a number here would then resolve the option's removal.
        const aiProposal = fieldProposals.find((p) => p.fieldName.value === row.fieldKey.value);
        return (
          <div key={row.fieldKey.value ?? "new"} css={Css.df.fdc.gap1.$}>
            <div css={Css.df.aic.gap1.$}>
              <div css={Css.fg1.$}>
                <BoundSelectField
                  label="Field"
                  labelStyle="hidden"
                  field={row.fieldKey}
                  options={programDataImpactFields.filter(
                    (f) => f.key === row.fieldKey.value || !usedKeys.includes(f.key),
                  )}
                  getOptionLabel={(f) => f.label}
                  getOptionValue={(f) => f.key}
                  onSelect={(key) => {
                    // Changing or removing the row walks away from whatever it was proposing
                    rejectAiProposal(aiProposal);
                    row.fieldKey.set(key);
                    // Reset values when the field changes, since the value type may change
                    row.impact.set(undefined);
                    row.elevations.set([]);
                  }}
                  disabled={disabled}
                />
              </div>
              <div css={Css.wPx(160).$}>
                <ImpactValueField
                  testId="impact"
                  field={field}
                  value={row.impact}
                  aiProposal={aiProposal}
                  enumDetails={enumDetails}
                  disabled={disabled}
                />
              </div>
              <ButtonMenu
                data-testid="impactMenu"
                trigger={{ icon: "verticalDots" }}
                disabled={disabled}
                items={[
                  {
                    label: "Remove",
                    onClick: () => {
                      rejectAiProposal(aiProposal);
                      impacts.remove(row.value);
                    },
                  },
                  // Elevations cannot modify themselves
                  ...(!option.type.isElevation
                    ? [
                        {
                          label: "Add Elevation Impact",
                          disabled:
                            (!row.fieldKey.value && "Select a field first") ||
                            (elevations.isEmpty && "There are no elevations to assign") ||
                            (row.elevations.rows.some((er) => !er.elevationId.value) &&
                              "Select an elevation for the new impact first"),
                          onClick: () => row.elevations.add({ elevationId: undefined, impact: undefined }),
                        },
                      ]
                    : []),
                ]}
              />
            </div>
            {row.elevations.rows.map((elevationRow) => {
              const usedElevationIds = row.elevations.rows.map((er) => er.elevationId.value).compact();
              return (
                <div key={elevationRow.elevationId.value ?? "new"} css={Css.df.aic.gap1.plPx(24).$}>
                  <Icon icon="subDirectoryRight" color={Palette.Gray400} />
                  <div css={Css.fg1.$}>
                    <BoundSelectField
                      label="Elevation"
                      labelStyle="hidden"
                      field={elevationRow.elevationId}
                      options={elevations.filter(
                        (e) => e.id === elevationRow.elevationId.value || !usedElevationIds.includes(e.id),
                      )}
                      getOptionLabel={(e) => `${e.code} - ${e.name}`}
                      getOptionValue={(e) => e.id}
                      disabled={disabled}
                    />
                  </div>
                  <div css={Css.wPx(160).$}>
                    <ImpactValueField
                      testId="elevationValue"
                      field={field}
                      value={elevationRow.impact}
                      enumDetails={enumDetails}
                      disabled={disabled}
                    />
                  </div>
                  <IconButton
                    data-testid="removeImpact"
                    icon="trash"
                    disabled={disabled}
                    onClick={() => row.elevations.remove(elevationRow.value)}
                  />
                </div>
              );
            })}
          </div>
        );
      })}
      <div>
        <Button
          label="Program Data Impact"
          icon="plus"
          variant="tertiary"
          disabled={
            disabled || (impacts.rows.some((row) => !row.fieldKey.value) && "Select a field for the new impact first")
          }
          onClick={() => impacts.add({ fieldKey: undefined, impact: undefined, elevations: [] })}
        />
      </div>
    </div>
  );
});

/** Renders the value input for an impact row, a number field with units or a dropdown for enum fields. */
function ImpactValueField(props: {
  field: Maybe<ProgramDataImpactField>;
  value: FieldState<Maybe<ImpactValue>>;
  aiProposal?: ObjectState<AiFieldProposalFormValue>;
  enumDetails: EnumDetails;
  disabled: ReactNode | boolean;
  testId?: string;
}) {
  const { field, value, aiProposal, enumDetails, disabled, testId } = props;
  const testIdProps = testId ? { "data-testid": testId } : {};
  if (field?.enumName) {
    return (
      <BoundSelectField
        {...testIdProps}
        label="Value"
        labelStyle="hidden"
        field={value as FieldState<Maybe<string>>}
        options={enumDetails[field.enumName] as { code: string; name: string }[]}
        getOptionLabel={(o) => o.name}
        getOptionValue={(o) => o.code}
        disabled={disabled}
      />
    );
  }
  return (
    <div css={Css.df.aic.gap1.$}>
      <BoundAiNumberField
        {...testIdProps}
        label="Value"
        labelStyle="hidden"
        field={value as FieldState<Maybe<number>>}
        aiProposal={aiProposal}
        displayDirection
        disabled={disabled || (!field && "Select a field first")}
      />
      {field?.unit && <span css={Css.xs.gray700.$}>{field.unit}</span>}
    </div>
  );
}

/**
 * The option's current impacts as form rows, i.e. one row per field it changes.
 *
 * `proposedFieldKeys` adds an empty row for a proposed field the option does not impact yet, so the
 * proposal has somewhere to render. The row stays empty until it is accepted, so an untouched one
 * is not dirty and saves nothing.
 */
export function toImpactRows(
  option: PlanPackageOptionsV2_OptionFragment,
  planPackage: PlanPackageOptionsV2_PlanPackageFragment,
  proposedFieldKeys: readonly string[] = [],
): ImpactRow[] {
  const base = baseProgramData(option);
  const modifiers = elevationModifiers(option, planPackage);
  return programDataImpactFields.flatMap((f) => {
    const impact = impactValue(base?.programData, f);
    const elevations = modifiers
      .map(({ elevationId, programData }) => ({ elevationId, impact: impactValue(programData, f) }))
      .filter((e) => e.impact != null);
    return impact != null || elevations.nonEmpty || proposedFieldKeys.includes(f.key)
      ? [{ fieldKey: f.key, impact, elevations }]
      : [];
  });
}

/** Diffs the impact rows against the option's current program data rows into save inputs. */
export function toProgramDataInputs(
  option: PlanPackageOptionsV2_OptionFragment,
  planPackage: PlanPackageOptionsV2_PlanPackageFragment,
  impacts: ImpactRow[],
): SaveReadyPlanProgramDataInput[] {
  const base = baseProgramData(option);
  const modifiers = elevationModifiers(option, planPackage);
  const rows = impacts.filter((row) => row.fieldKey);
  function diff(
    before: Maybe<PlanPackageOptionsV2_ProgramDataFragment>,
    after: (field: ProgramDataImpactField) => Maybe<ImpactValue>,
  ): SaveProgramDataInput {
    // Only send the fields that actually changed, where a cleared field nulls it out
    return Object.fromEntries(
      programDataImpactFields
        .filter((field) => (impactValue(before, field) ?? null) !== (after(field) ?? null))
        .map((field) => [field.key, after(field) ?? null]),
    );
  }
  // Diff the base row's fields against the impact rows' base values
  const basePatch = diff(base?.programData, (f) => rows.find((row) => row.fieldKey === f.key)?.impact);
  // Diff each elevation's modifier row, across both existing modifiers and newly added elevation impacts
  const elevationIds = [
    ...modifiers.map((m) => m.elevationId),
    ...rows.flatMap((row) => row.elevations.map((e) => e.elevationId)).compact(),
  ].unique();
  const modifierInputs = elevationIds.compact().flatMap((elevationId) => {
    const existing = modifiers.find((m) => m.elevationId === elevationId);
    const patch = diff(
      existing?.programData,
      (field) =>
        rows.find((row) => row.fieldKey === field.key)?.elevations.find((e) => e.elevationId === elevationId)?.impact,
    );
    if (Object.keys(patch).isEmpty) return [];
    return [
      existing
        ? { id: existing.id, op: IncrementalCollectionOp.Include, programData: patch }
        : { op: IncrementalCollectionOp.Include, readyPlanOptionIds: [option.id, elevationId], programData: patch },
    ];
  });
  return [
    ...(Object.keys(basePatch).nonEmpty
      ? [{ id: base?.id, op: IncrementalCollectionOp.Include, readyPlanOptionIds: [option.id], programData: basePatch }]
      : []),
    ...modifierInputs,
  ];
}

/** The option's own program data row, i.e. the one that references only it. */
function baseProgramData(option: PlanPackageOptionsV2_OptionFragment) {
  return option.programDatas.find((pd) => pd.readyPlanOptions.length === 1);
}

/** The option's per-elevation modifier rows, i.e. program data rows shared with exactly one elevation. */
function elevationModifiers(
  option: PlanPackageOptionsV2_OptionFragment,
  planPackage: PlanPackageOptionsV2_PlanPackageFragment,
) {
  const elevationIds = planPackage.options.filter((o) => o.type.isElevation).map((o) => o.id);
  return option.programDatas.flatMap((pd) => {
    if (pd.readyPlanOptions.length !== 2) return [];
    const otherId = pd.readyPlanOptions.find((ro) => ro.id !== option.id)?.id;
    if (!otherId || !elevationIds.includes(otherId)) return [];
    return [{ id: pd.id, elevationId: otherId, programData: pd.programData }];
  });
}

/** Reads a field's value off the program data fragment, unwrapping enum details to their code. */
function impactValue(
  pd: Maybe<PlanPackageOptionsV2_ProgramDataFragment>,
  field: ProgramDataImpactField,
): Maybe<ImpactValue> {
  const value = pd?.[field.key];
  return typeof value === "object" && value !== null ? value.code : value;
}

/**
 * The program data fields an option can impact, i.e. the writable `SaveProgramDataInput` fields, with
 * their user facing label, unit, and optionally the name of their enum detail to populate dropdowns.
 *
 * Note `sellableSqft` and `unfinishedBelowGroundSqft` are BE derived and cannot be set.
 */
const programDataImpactFields: ProgramDataImpactField[] = [
  { key: "widthInFeet", label: "Plan Width", unit: "FT" },
  { key: "depthInFeet", label: "Plan Depth", unit: "FT" },
  { key: "buildingHeightInFeet", label: "Building Height", unit: "FT" },
  { key: "stories", label: "Stories" },
  { key: "minLotSizeInSqft", label: "Min Lot Size", unit: "SF" },
  { key: "minLotWidthInFeet", label: "Min Lot Width", unit: "FT" },
  { key: "minLotDepthInFeet", label: "Min Lot Depth", unit: "FT" },
  { key: "netBuildableSqft", label: "Net SF", unit: "SF" },
  { key: "grossBuildableSqft", label: "Gross Buildable SF", unit: "SF" },
  { key: "permittableSqft", label: "Permittable SF", unit: "SF" },
  { key: "sellableAboveGroundSqft", label: "Sellable Above Ground SF", unit: "SF" },
  { key: "sellableBelowGroundSqft", label: "Sellable Below Ground SF", unit: "SF" },
  { key: "grossBelowGroundSqft", label: "Gross Below Ground SF", unit: "SF" },
  { key: "imperviousSqft", label: "Impervious SF", unit: "SF" },
  { key: "bedrooms", label: "Bedrooms" },
  { key: "firstFloorBedrooms", label: "First Floor Bedrooms" },
  { key: "closetsInPrimarySuite", label: "Closets in Primary Suite" },
  { key: "fullBaths", label: "Full Baths" },
  { key: "halfBaths", label: "Half Baths" },
  { key: "diningRoom", label: "Formal Dining" },
  { key: "casualDining", label: "Casual Dining" },
  { key: "mediaRooms", label: "Media Rooms" },
  { key: "loftGameFlexRooms", label: "Lofts/Game Rooms/Flex Rooms" },
  { key: "offices", label: "Offices" },
  { key: "workspaces", label: "Workspaces" },
  { key: "garageAttached", label: "Garage Attached" },
  { key: "garageDetached", label: "Garage Detached" },
  { key: "garagePort", label: "Carport" },
  { key: "garageConfiguration", label: "Garage Load", enumName: "programDataGarageConfig" },
  { key: "basementConfig", label: "Basement", enumName: "programDataBasementConfig" },
  { key: "primaryBedroom", label: "Primary Bedroom Location", enumName: "programDataPrimaryBedroomConfig" },
  { key: "windowColor", label: "Window Color", enumName: "programDataWindowColor" },
];
